/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-07
 */
import {
  NebulaExportFormat,
  NebulaExportResult,
  NebulaExportSource,
  NebulaTableQuery,
} from 'nebula-ui-kit';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { HttpService } from '../http/http.service';

/**
 * Origen por defecto de la exportacion sobre el `POST <ruta>/export` canonico de Simappe
 * (HT_ARQ_013, RN-TAR-079): envia la MISMA consulta de la grilla (RN-TAR-077) y el `formato`.
 *
 * <p>Dos respuestas (RN-TAR-078): en linea llega el archivo; cuando se crea tarea llega el
 * `ExportResponse` como JSON, con su `jobId`.</p>
 *
 * <p>Generico sobre la respuesta JSON del servicio (`R`): la pantalla le dice al dialogo como
 * leerla (orden de Carlos del 15-oct-2026).</p>
 */
export class OrigenExportacionSimappe<R = NebulaExportResult> implements NebulaExportSource<R> {
  /**
   * @param http cliente HTTP del ERP.
   * @param ruta ruta del recurso, con el prefijo del servicio.
   */
  constructor(
    private readonly http: HttpService,
    private readonly ruta: string,
  ) {}

  /** @inheritdoc */
  exportar(
    consulta: NebulaTableQuery,
    formato: NebulaExportFormat,
    parametros?: Record<string, unknown>,
  ): Observable<R | NebulaExportResult> {
    return this.http
      .postBlob(`${this.ruta}/export`, consulta, { ...parametros, formato })
      .pipe(switchMap((contenido) => interpretar<R>(contenido)));
  }
}

/**
 * Distingue el archivo de la respuesta JSON por su tipo de contenido. El archivo llega como
 * resultado con `archivo`; el JSON se entrega sin el envoltorio de la plataforma, tal como llego,
 * para que el dialogo lo lea con la lectura de la pantalla o con la estandar.
 *
 * @param contenido lo que respondio el servicio.
 * @returns el archivo, o la respuesta del servicio sin envoltorio.
 */
function interpretar<R>(contenido: Blob): Observable<R | NebulaExportResult> {
  if (!contenido.type.includes('application/json')) {
    return of({ jobId: null, archivo: contenido });
  }
  return from(contenido.text()).pipe(
    map((texto) => {
      const cuerpo = JSON.parse(texto) as { response?: { content?: R }; content?: R } & R;
      return cuerpo.response?.content ?? cuerpo.content ?? cuerpo;
    }),
  );
}
